/* eslint-disable jsx-a11y/alt-text */
import React, { useState, useEffect } from "react";
import { 
  Box, 
  Button, 
  TextField, 
  Typography, 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";
import "./book-now.css";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { createStripeCheckoutSession } from "../../api/payment";
import { useToast } from "../../components/toaster";
import "react-phone-number-input/style.css";
import Loader from "../../components/loader";
import { publicListOfLockedDates } from "../../api/dates-api";

const BookingComponent = () => {
  const [participants, setParticipants] = useState(2);
  const [registrationDate, setRegistrationDate] = useState(dayjs());
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [errors, setErrors] = useState({});
  const [loader, setLoader] = useState(false);
  const [disableDates, setDisableDates] = useState([]);
  const { showToast } = useToast();
  const [globalLoader, setGlobalLoader] = useState(false);
  
  // New state for duplicate booking dialog
  const [showDuplicateDialog, setShowDuplicateDialog] = useState(false);
  const [duplicateMessage, setDuplicateMessage] = useState("");
  const [pendingBookingData, setPendingBookingData] = useState(null);

  useEffect(() => {
    setGlobalLoader(true);
    publicListOfLockedDates()
      .then((response) => {
        const { data = {} } = response;
        const { lockedDates = [] } = data;
        setDisableDates(lockedDates);
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        setGlobalLoader(false);
      });
  }, []);

  const isDisabled = (date) => {
    return disableDates.includes(date.format("YYYY-MM-DD"));
  };

  const pricePerPerson = 20;
  const total =
    typeof participants === "number" ? pricePerPerson * participants : "?";

  const handleError = (key, value) => {
    let message = "";
    if (!value || (typeof value === "string" && value?.trim() === "")) {
      const errorKeysName = {
        participants: "Number of participants",
        fullName: "Full Name",
        email: "Email Address",
      };
      message = `${errorKeysName[key]} is required.`;
    } else if (key === "email" && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
      message = "Enter a valid email address.";
    } else if (key === "participants" && Number(value) <= 1) {
      message = "Minimum 2 participants are required";
    } else if (
      key === "fullName" &&
      (typeof value !== "string")
    ) {
      message = "Enter a valid name.";
    }
    setErrors((prev) => ({
      ...prev,
      [key]: message,
    }));
  };

  const isFormValid = () => {
    return (
      Number(participants) > 1 &&
      fullName?.trim() !== "" &&
      email?.trim() !== "" &&
      registrationDate &&
      Object.values(errors).every((val) => val === "")
    );
  };

  const processPayment = async (allowDuplicate = false) => {
    try {
      setLoader(true);
      
      const bookingData = {
        email,
        registrationDate: registrationDate?.format('YYYY-MM-DD'),
        teamMemberCount: participants,
        amount: total,
        fullName,
        allowDuplicate, // Add the allowDuplicate flag
      };

      const checkoutSessionResponse = await createStripeCheckoutSession(bookingData);
      
      if (checkoutSessionResponse.status === 200) {
        const {
          data: { checkoutUrl },
        } = checkoutSessionResponse;
        window.location.href = checkoutUrl;
      }
    } catch (error) {
      if (error.status === 409) {
        const errorData = error?.response?.data;
        // Check if this is a duplicate booking error
        if (errorData?.message === "DUPLICATE_BOOKING") {
          // Store the booking data for later use
          setPendingBookingData({
            email,
            registrationDate: registrationDate?.format('YYYY-MM-DD'),
            teamMemberCount: participants,
            amount: total,
            fullName,
          });
          const msg = "You already have a booking for this date - would you like to proceed with a new one?";
          setDuplicateMessage(msg);
          setShowDuplicateDialog(true);
        } else {
          // Handle other 409 errors
          showToast(errorData?.message || "An error occurred", "error");
        }
      } else {
        // Handle other errors
        showToast("An error occurred. Please try again.", "error");
      }
    } finally {
      setLoader(false);
    }
  };

  const handleOnClick = async (e) => {
    e.preventDefault();
    await processPayment(false); // Initial attempt without allowing duplicates
  };

  const handleConfirmDuplicate = async () => {
    setShowDuplicateDialog(false);
    await processPayment(true); // Allow duplicate booking
  };

  const handleCancelDuplicate = () => {
    setShowDuplicateDialog(false);
    setPendingBookingData(null);
    setDuplicateMessage("");
  };

  return (
    <>
      {globalLoader ? <Loader variant={'overlay'} /> :
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Box className="card-main">
              <Box className="booking-wrapper">
                <Typography variant="h4" className="form-title">
                  BOOK <em>NOW</em>
                </Typography>

                <div className="form-section-main">
                  <div className="form-section">
                    <Typography className="form-label">
                      Number of participants:
                    </Typography>
                    <TextField
                      type="number"
                      placeholder="Number of participants"
                      fullWidth
                      value={participants === null ? "" : participants}
                      onChange={(e) => {
                        let value = e.target.value;
                        if (value === "") {
                          setParticipants(null);
                          return;
                        }
                        if (/^0\d+/.test(value)) return;
                        if (value.length > 10) return;
                        if (Number(value) <= 0) return;
                        setParticipants(Number(value));
                        handleError("participants", value);
                      }}
                      onBlur={(e) => handleError("participants", e.target.value)}
                      error={!!errors.participants}
                      helperText={errors.participants}
                      sx={{
                        "& .MuiFormHelperText-root": {
                          color: "error.main",
                          marginLeft: 0.5,
                          fontSize: "0.75rem",
                        },
                      }}
                    />
                  </div>

                  <div className="form-section">
                    <Typography className="form-label">
                      Date of adventure:
                    </Typography>
                    <DatePicker
                      value={registrationDate}
                      minDate={dayjs()}
                      format="DD/MM/YYYY"
                      shouldDisableDate={isDisabled}
                      onChange={(newValue) => setRegistrationDate(newValue)}
                      className="form-input"
                    />
                  </div>

                  <div className="form-section">
                    <Typography className="form-label">Full Name</Typography>
                    <TextField
                      placeholder="Full name"
                      fullWidth
                      value={fullName}
                      onChange={(e) => {
                        setFullName(e.target.value);
                        handleError("fullName", e.target.value);
                      }}
                      onBlur={(e) => handleError("fullName", e.target.value)}
                      error={!!errors["fullName"]}
                      helperText={errors["fullName"]}
                      sx={{
                        "& .MuiFormHelperText-root": {
                          color: "error.main",
                          marginLeft: 0.5,
                          fontSize: "0.75rem",
                        },
                      }}
                    />
                  </div>
                  <Box className="form-section">
                    <Typography className="form-label">Email Address</Typography>
                    <TextField
                      placeholder="Email Address"
                      type="email"
                      fullWidth
                      value={email}
                      onChange={(e) => {
                        setEmail(e.target.value);
                        handleError("email", e.target.value);
                      }}
                      onBlur={(e) => handleError("email", e.target.value)}
                      error={!!errors["email"]}
                      helperText={errors["email"]}
                      sx={{
                        "& .MuiFormHelperText-root": {
                          color: "error.main",
                          marginLeft: 0.5,
                          fontSize: "0.75rem",
                        },
                      }}
                    />
                  </Box>
                </div>
                <div className="form-section-price">
                  <Typography
                    className="form-label-price"
                    variant="subtitle2"
                    sx={{
                      color: "gray",
                      fontWeight: 600,
                      letterSpacing: "0.05em",
                      marginRight: 2,
                    }}
                  >
                    TOTAL PRICE:
                  </Typography>

                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1.5,
                      color: "black",
                    }}
                  >
                    <Typography
                      color="black"
                      variant="body1"
                      sx={{
                        fontWeight: 500,
                        fontSize: { xs: "0.8rem", sm: "0.9rem" },
                      }}
                    >
                      £20 × {participants || "0"}
                    </Typography>

                    <Box component="span">=</Box>

                    <Typography
                      variant="h6"
                      sx={{ fontSize: { xs: "14px", sm: "1rem" } }}
                    >
                      £{total || "0"}
                    </Typography>
                  </Box>
                </div>
                <Box sx={{ display: "flex", width: "100%" }}>
                  <Button
                    className="pay-button"
                    sx={{ width: "100%", color: "black", fontWeight: 700 }}
                    disabled={!isFormValid()}
                    onClick={handleOnClick}
                  >
                    {loader ? <Loader color={'#FFFFFF'} /> : 'PAY NOW'}
                  </Button>
                </Box>
              </Box>
              <Box
                sx={{
                  width: 10,
                  height: 460,
                  display: { lg: "flex", xs: "none" },
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                {/* <img src="/logo.png" style={{maxWidth: 360, maxHeight: 360, width: 'auto', height: 'auto'}} /> */}
              </Box>
            </Box>
        </LocalizationProvider>
      }

      {/* Duplicate Booking Confirmation Dialog */}
      <Dialog
        open={showDuplicateDialog}
        onClose={handleCancelDuplicate}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            padding: 2,
          }
        }}
      >
        <DialogTitle sx={{ 
          fontSize: '1.5rem', 
          fontWeight: 'bold',
          color: 'primary.main',
          textAlign: 'center',
          pb: 1
        }}>
          Duplicate Booking
        </DialogTitle>
        
        <DialogContent sx={{ textAlign: 'center', py: 2 }}>
          <Typography variant="body1" sx={{ 
            fontSize: '1.1rem', 
            color: 'text.secondary',
            lineHeight: 1.6
          }}>
            {duplicateMessage}
          </Typography>
        </DialogContent>
        
        <DialogActions sx={{ 
          justifyContent: 'center', 
          gap: 2, 
          pb: 2,
          pt: 1
        }}>
          <Button
            onClick={handleCancelDuplicate}
            variant="outlined"
            sx={{ 
              minWidth: 100,
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDuplicate}
            variant="contained"
            sx={{ 
              minWidth: 100,
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
              bgcolor: 'primary.main',
              '&:hover': {
                bgcolor: 'primary.dark',
              }
            }}
            disabled={loader}
          >
            {loader ? <Loader color={'#FFFFFF'} size={20} /> : 'Yes, Proceed'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default BookingComponent;
